import { fail } from '../../Effect/dist/effect-Effect.esm.js';
import { StructuralCommitPrototype } from '../../Effectable/dist/effect-Effectable.esm.js';
import { struct as struct$1, StructProto, ArrayProto, Structural as Structural$1 } from '../../internal/Data.esm.js';

/**
 * @since 2.0.0
 */

/**
 * @category models
 * @since 2.0.0
 */

/**
 * `Case` represents a datatype similar to a case class in Scala. Namely, a
 * datatype created using `Case` will, by default, provide an implementation
 * for a constructor, `Hash`, and `Equal`.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 */

/**
 * @category constructors
 * @since 2.0.0
 */
const struct = struct$1;

/**
 * @category constructors
 * @since 2.0.0
 */
const unsafeStruct = as => Object.setPrototypeOf(as, StructProto);

/**
 * @category constructors
 * @since 2.0.0
 */
const tuple = (...as) => unsafeArray(as);

/**
 * @category constructors
 * @since 2.0.0
 */
const array = as => unsafeArray(as.slice(0));

/**
 * @category constructors
 * @since 2.0.0
 */
const unsafeArray = as => Object.setPrototypeOf(as, ArrayProto);
const _case = () => args => args === undefined ? Object.create(StructProto) : struct(args);

/**
 * Provides a tagged constructor for the specified `Case`.
 *
 * @since 2.0.0
 * @category constructors
 */
const tagged = tag => args => {
  const value = args === undefined ? Object.create(StructProto) : struct(args);
  value._tag = tag;
  return value;
};

/**
 * Provides a Tagged constructor for a Case Class.
 *
 * @since 2.0.0
 * @category constructors
 */
const TaggedClass = tag => {
  class Base extends Class {
    _tag = tag;
  }
  return Base;
};

/**
 * Provides a constructor for a Case Class.
 *
 * @since 2.0.0
 * @category constructors
 */
const Class = Structural$1;

/**
 * @since 2.0.0
 * @category constructors
 */
const Structural = Structural$1;

/**
 * Create a tagged enum data type, which is a union of `Data` structs.
 *
 * ```ts
 * import * as Data from "effect/Data"
 *
 * type HttpError = Data.TaggedEnum<{
 *   BadRequest: { status: 400, message: string }
 *   NotFound: { status: 404, message: string }
 * }>
 *
 * // Equivalent to:
 * type HttpErrorPlain =
 *   | Data.Data<{
 *     readonly _tag: "BadRequest"
 *     readonly status: 400
 *     readonly message: string
 *   }>
 *   | Data.Data<{
 *     readonly _tag: "NotFound"
 *     readonly status: 404
 *     readonly message: string
 *   }>
 * ```
 *
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 */

/**
 * Create a constructor for a tagged union of `Data` structs.
 *
 * You can also pass a `TaggedEnum.WithGenerics` if you want to add generics to
 * the constructor.
 *
 * @example
 * import * as Data from "effect/Data"
 *
 * const HttpError = Data.taggedEnum<
 *   | Data.Data<{ _tag: "BadRequest"; status: 400; message: string }>
 *   | Data.Data<{ _tag: "NotFound"; status: 404; message: string }>
 * >()
 *
 * const notFound = HttpError("NotFound")({ status: 404, message: "Not Found" })
 *
 * @example
 * import * as Data from "effect/Data"
 *
 * type MyResult<E, A> = Data.TaggedEnum<{
 *   Failure: { error: E }
 *   Success: { value: A }
 * }>
 * interface MyResultDefinition extends Data.TaggedEnum.WithGenerics<2> {
 *   readonly taggedEnum: MyResult<this["A"], this["B"]>
 * }
 * const MyResult = Data.taggedEnum<MyResultDefinition>()
 *
 * const success = MyResult("Success")({ value: 1 })
 *
 * @category constructors
 * @since 2.0.0
 */
const taggedEnum = () => tagged;

/**
 * @since 2.0.0
 * @category models
 */

const YieldableErrorMessage = /*#__PURE__*/Symbol.for("effect/Data/YieldableError/message");
const YieldableErrorProto = {
  ...StructuralCommitPrototype,
  __proto__: globalThis.Error.prototype,
  commit() {
    return fail(this);
  },
  toString() {
    return `${this.name}: ${this.message}`;
  },
  get message() {
    return this[YieldableErrorMessage] ?? JSON.stringify(this);
  },
  set message(value) {
    this[YieldableErrorMessage] = value;
  }
};

/**
 * Provides a constructor for a Case Class.
 *
 * @since 2.0.0
 * @category constructors
 */
const Error = /*#__PURE__*/function () {
  function Base(args) {
    if (args) {
      Object.assign(this, args);
    }
    globalThis.Error.captureStackTrace(this, this.constructor);
  }
  Base.prototype = YieldableErrorProto;
  return Base;
}();

/**
 * @since 2.0.0
 * @category constructors
 */
const TaggedError = tag => {
  class Base extends Error {
    _tag = tag;
  }
  Base.prototype.name = tag;
  return Base;
};

export { Class, Error, Structural, TaggedClass, TaggedError, array, _case as case, struct, tagged, taggedEnum, tuple, unsafeArray, unsafeStruct };
